import { useCallback, useEffect, useState } from 'react'
import { api, dropNulls } from './api'
import type { HarvestLog } from './types'

export type CreateHarvestLogInput = {
  fieldId: string
  seasonYear: number
  productVariety: string
  phiVerified: boolean
  siteAssessed: boolean
  harvestedOn: string
  quantityHarvested: number
  quantityUnit: string
  packagingMaterials?: string | null
  storedOn?: string | null
  notes?: string | null
}

export type UpdateHarvestLogInput = Partial<Omit<CreateHarvestLogInput, 'fieldId' | 'seasonYear'>>

export function useHarvestLogs(
  orgId: string | undefined,
  farmId: string | undefined,
  fieldId: string | undefined,
  seasonYear: number,
) {
  const [logs, setLogs] = useState<HarvestLog[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const base = orgId && farmId ? `/orgs/${orgId}/farms/${farmId}/harvest-logs` : null

  const loadLogs = useCallback(async () => {
    if (!base || !fieldId) return
    setLoading(true)
    setError(null)
    try {
      const { data } = await api.getPage<HarvestLog>(`${base}?fieldId=${fieldId}&seasonYear=${seasonYear}`)
      setLogs(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load harvest logs.')
    } finally {
      setLoading(false)
    }
  }, [base, fieldId, seasonYear])

  useEffect(() => {
    loadLogs()
  }, [loadLogs])

  async function addLog(input: CreateHarvestLogInput) {
    if (!base) return
    const created = await api.post<HarvestLog>(base, dropNulls(input))
    setLogs((prev) => [created, ...prev])
    return created
  }

  async function updateLog(id: string, patch: UpdateHarvestLogInput) {
    if (!base) return
    const updated = await api.patch<HarvestLog>(`${base}/${id}`, patch)
    setLogs((prev) => prev.map((l) => (l.id === id ? updated : l)))
    return updated
  }

  return { logs, loading, error, addLog, updateLog }
}
